<template>
  <div class="my-products-page">
    <!-- 顶部导航 -->
    <header class="page-header">
      <div class="header-left" @click="goBack">
        <span class="back-icon">←</span>
        <span>{{ $t('common.back') }}</span>
      </div>
      <h1 class="page-title">{{ $t('wholesaler.MyProducts.line9_text') }}</h1>
      <button class="header-btn">{{ $t('wholesaler.MyProducts.line10_text') }}</button>
    </header>

    <!-- 统计卡片 -->
    <div class="stats-row">
      <div class="stat-card">
        <div class="stat-value" style="color: #667eea;">{{ products.length }}</div>
        <div class="stat-label">{{ $t('common.products') }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-value" style="color: #52c41a;">{{ wholesalePriceCount }}</div>
        <div class="stat-label">{{ $t('wholesaler.MyProducts.line21_text') }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-value" style="color: #1890ff;">{{ stallCount }}</div>
        <div class="stat-label">{{ $t('wholesaler.MyProducts.line25_text') }}</div>
      </div>
    </div>

    <!-- 分类导航 + 批量操作 -->
    <div class="category-bar">
      <!-- 左侧分类 -->
      <div class="category-list">
        <div 
          :class="['category-item', { active: selectedCategory === 'all' }]"
          @click="selectedCategory = 'all'"
        >
          <span class="category-icon">🍎</span>
          <span>{{ $t('wholesaler.MyProducts.line38_text') }}</span>
        </div>
        <div 
          :class="['category-item', { active: selectedCategory === 'fruit' }]"
          @click="selectedCategory = 'fruit'"
        >
          <span class="category-icon">🥦</span>
        </div>
        <div 
          :class="['category-item', { active: selectedCategory === 'meat' }]"
          @click="selectedCategory = 'meat'"
        >
          <span class="category-icon">🥩</span>
        </div>
        <div 
          :class="['category-item', { active: selectedCategory === 'fish' }]"
          @click="selectedCategory = 'fish'"
        >
          <span class="category-icon">🐟</span>
        </div>
        <div 
          :class="['category-item', { active: selectedCategory === 'vegetable' }]"
          @click="selectedCategory = 'vegetable'"
        >
          <span class="category-icon">🌽</span>
        </div>
      </div>

      <!-- 批量操作栏 -->
      <div class="batch-actions">
        <label class="select-all">
          <input type="checkbox" v-model="selectAll" />
          <span>{{ $t('wholesaler.MyProducts.line70_text') }}</span>
        </label>
        <button class="action-btn上架" @click="batchUpdateStatus(true)">{{ $t('wholesaler.MyProducts.line72_text') }}</button>
        <button class="action-btn 下架" @click="batchUpdateStatus(false)">{{ $t('wholesaler.MyProducts.line73_text') }}</button>
        <button class="action-btn 调价" @click="batchAdjustPrice">{{ $t('wholesaler.MyProducts.line74_text') }}</button>
        <button class="action-btn 批发价" @click="batchSetWholesalePrice">{{ $t('wholesaler.MyProducts.line75_text') }}</button>
        <button class="action-btn 代客下单" @click="batchOrderForCustomer">{{ $t('wholesaler.MyProducts.line76_text') }}</button>
      </div>
    </div>

    <!-- 商品列表 -->
    <div class="product-list">
      <div 
        v-for="product in products" 
        :key="product.id"
        :class="['product-card', { selected: product.selected }]"
      >
        <!-- 左侧复选框 -->
        <div class="product-checkbox">
          <input type="checkbox" v-model="product.selected" />
        </div>

        <!-- 商品信息 -->
        <div class="product-content">
          <!-- 商品名称和分类 -->
          <div class="product-header">
            <h3 class="product-name">{{ product.name }}</h3>
            <p class="product-meta">
              <span>{{ product.category || '水果类' }}</span>
              <span>|</span>
              <span>{{ product.sku || 'PROD001' }}</span>
            </p>
          </div>

          <!-- 商品图片和价格 -->
          <div class="product-main">
            <div class="product-image">
              <img :src="getImageUrl(getMainImage(product))" :alt="$t('wholesaler.MyProducts.line107_alt')" />
            </div>

            <div class="product-prices">
              <div class="price-row">
                <span class="price-label">{{ $t('wholesaler.MyProducts.line112_text') }}</span>
                <span class="market-price">¥{{ formatPrice(product.price || product.basePrice) }}</span>
              </div>
              <div class="price-row">
                <span class="price-label">{{ $t('wholesaler.MyProducts.line116_text') }}</span>
                <span class="wholesale-price">¥{{ formatPrice(product.wholesalePrice || product.price * 0.8) }}</span>
              </div>
            </div>
          </div>

          <!-- 批发价状态 -->
          <div class="wholesale-status">
            <span class="status-icon">🏪</span>
            <span class="status-text">{{ $t('wholesaler.MyProducts.line125_text') }}</span>
            <span class="status-count">{{ $t('wholesaler.MyProducts.line126_text') }}</span>
          </div>

          <!-- 商品标签 -->
          <div class="product-tags">
            <span class="tag tag-stock">📦 {{ product.currentStock || product.stock || 0 }}{{ product.unit || '件' }}</span>
            <span class="tag tag-warehouse">{{ $t('wholesaler.MyProducts.line132_text') }}</span>
            <span class="tag tag-status" :class="{ active: product.enabled }">
              {{ product.enabled ? '✓' : '' }} {{ product.enabled ? '上架' : '下架' }}
            </span>
          </div>

          <!-- 操作按钮 -->
          <div class="product-actions">
            <button class="btn-edit" @click="editProduct(product)">{{ $t('common.edit') }}</button>
            <button class="btn-wholesale" @click="manageWholesalePrice(product)">{{ $t('wholesaler.MyProducts.line141_text') }}</button>
            <button class="btn-order" @click="orderForCustomer(product)">{{ $t('wholesaler.MyProducts.line142_text') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部导航 -->
    <nav class="bottom-nav">
      <div class="nav-item" @click="$router.push('/wholesaler/home')">
        <span class="nav-icon">🏠</span>
        <span>{{ $t('common.home') }}</span>
      </div>
      <div class="nav-item active">
        <span class="nav-icon">📦</span>
        <span>{{ $t('common.products') }}</span>
      </div>
      <div class="nav-item" @click="$router.push('/wholesaler/statistics')">
        <span class="nav-icon">📊</span>
        <span>{{ $t('wholesaler.MyProducts.line160_text') }}</span>
      </div>
      <div class="nav-item" @click="$router.push('/my-profile')">
        <span class="nav-icon">👤</span>
        <span>{{ $t('common.profile') }}</span>
      </div>
    </nav>
  </div>
</template>

<script>
import api from '@/services/api';
import { hasPermission } from '@/utils/permission';
import { getWholesalerId } from '@/utils/wholesaler';
import { getImageUrl } from '@/utils/image';

export default {
  name: 'MyProducts',
  data() {
    return {
      products: [],
      loading: false,
      selectedCategory: 'all',
      selectAll: false,
      wholesalePriceCount: 0,
      stallCount: 0
    };
  },
  mounted() {
    this.loadProducts();
  },
  methods: {
    getImageUrl,
    hasPermission,

    async loadProducts() {
      this.loading = true;
      try {
        const wholesalerId = this.getWholesalerId();
        const result = await api.get(`/product/wholesaler/list/${wholesalerId}`);
        
        if (Array.isArray(result)) {
          this.products = result.map(p => ({
            ...p,
            selected: false
          }));
          
          // 统计已设批发价的商品数
          this.wholesalePriceCount = this.products.filter(p => p.wholesalePrice).length;
          // 统计档口数（示例数据）
          this.stallCount = 25;
        }
      } catch (error) {
        console.error('加载商品失败:', error);
      } finally {
        this.loading = false;
      }
    },

    getWholesalerId() {
      return getWholesalerId() ?? 1;
    },

    formatPrice(price) {
      if (price === null || price === undefined) return '0.00';
      return Number(price).toFixed(2);
    },

    getMainImage(product) {
      if (product.mainImages && product.mainImages.length > 0) {
        return product.mainImages[0];
      }
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjVmNWY1Ii8+PHRleHQgeD0iNTAiIHk9IjUwIiBmaWxsPSIjOTk5IiBmb250LXNpemU9IjE2IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+5r+0r+eOvTwvdGV4dD48L3N2Zz4=';
    },

    goBack() {
      this.$router.back();
    },

    batchUpdateStatus(enabled) {
      const selectedProducts = this.products.filter(p => p.selected);
      if (selectedProducts.length === 0) {
        alert('请先选择商品');
        return;
      }
      console.log(`批量${enabled ? '上架' : '下架'}:`, selectedProducts);
    },

    batchAdjustPrice() {
      const selectedProducts = this.products.filter(p => p.selected);
      if (selectedProducts.length === 0) {
        alert('请先选择商品');
        return;
      }
      console.log('批量调价:', selectedProducts);
    },

    batchSetWholesalePrice() {
      const selectedProducts = this.products.filter(p => p.selected);
      if (selectedProducts.length === 0) {
        alert('请先选择商品');
        return;
      }
      console.log('批量设置批发价:', selectedProducts);
    },

    batchOrderForCustomer() {
      const selectedProducts = this.products.filter(p => p.selected);
      if (selectedProducts.length === 0) {
        alert('请先选择商品');
        return;
      }
      // 跳转到代客下单页面，带上选中的商品
      this.$router.push({
        path: '/wholesaler/order-on-behalf',
        query: { productIds: selectedProducts.map(p => p.id).join(',') }
      });
    },

    orderForCustomer(product) {
      // 单个商品代客下单
      this.$router.push({
        path: '/wholesaler/order-on-behalf',
        query: { productIds: product.id.toString() }
      });
    },

    editProduct(product) {
      console.log('编辑商品:', product);
      this.$router.push(`/wholesaler/product/${product.id}/edit`);
    },

    manageWholesalePrice(product) {
      console.log('管理批发价:', product);
      this.$router.push(`/wholesaler/product/${product.id}/wholesale-price`);
    }
  },
  watch: {
    selectAll(newVal) {
      this.products.forEach(p => p.selected = newVal);
    }
  }
};
</script>

<style scoped>
.my-products-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 60px;
}

/* ========== 顶部导航 ========== */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.back-icon {
  font-size: 20px;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.header-btn {
  background: rgba(255,255,255,0.2);
  border: none;
  color: white;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
}

/* ========== 统计卡片 ========== */
.stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 12px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  text-align: center;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12px;
  color: #999;
}

/* ========== 分类导航 + 批量操作 ========== */
.category-bar {
  background: white;
  padding: 12px;
  margin-bottom: 12px;
}

.category-list {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.category-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 12px;
  background: #f5f5f5;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.category-item.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.category-icon {
  font-size: 20px;
  margin-bottom: 4px;
}

.category-item span:last-child {
  font-size: 12px;
}

.batch-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #f9f9f9;
  border-radius: 8px;
}

.select-all {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 600;
}

.action-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  cursor: pointer;
}

.action-btn.上架 {
  background: #52c41a;
}

.action-btn.下架 {
  background: #d9d9d9;
  color: #666;
}

.action-btn.调价 {
  background: #fa8c16;
}

.action-btn.批发价 {
  background: #722ed1;
}

.action-btn.代客下单 {
  background: #52c41a;
}

/* ========== 商品列表 ========== */
.product-list {
  padding: 0 12px;
}

.product-card {
  background: white;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 12px;
  display: flex;
  gap: 12px;
  border-left: 4px solid transparent;
}

.product-card.selected {
  border-left-color: #52c41a;
}

.product-checkbox {
  display: flex;
  align-items: flex-start;
  padding-top: 4px;
}

.product-checkbox input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: #52c41a;
}

.product-content {
  flex: 1;
}

.product-header {
  margin-bottom: 12px;
}

.product-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px;
}

.product-meta {
  font-size: 12px;
  color: #999;
  margin: 0;
}

.product-meta span {
  margin: 0 4px;
}

.product-main {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.product-image {
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f5f5;
  flex-shrink: 0;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-prices {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
}

.price-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.price-label {
  font-size: 12px;
  color: #999;
}

.market-price {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.wholesale-price {
  font-size: 18px;
  font-weight: 700;
  color: #ff4d4f;
}

.wholesale-status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 8px;
  margin-bottom: 8px;
}

.status-icon {
  font-size: 16px;
}

.status-text {
  font-size: 13px;
  color: #52c41a;
  font-weight: 600;
}

.status-count {
  font-size: 12px;
  color: #999;
  margin-left: auto;
}

.product-tags {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.tag {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
}

.tag-stock {
  background: #e6f4ff;
  color: #1890ff;
}

.tag-warehouse {
  background: #fff2f0;
  color: #ff4d4f;
}

.tag-status {
  background: #f5f5f5;
  color: #999;
}

.tag-status.active {
  background: #f6ffed;
  color: #52c41a;
}

.product-actions {
  display: flex;
  gap: 8px;
}

.btn-edit {
  flex: 1;
  padding: 10px;
  background: #f5f5f5;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
}

.btn-wholesale {
  flex: 1.5;
  padding: 10px;
  background: #52c41a;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  color: white;
  font-weight: 600;
  cursor: pointer;
}

.btn-order {
  flex: 1;
  padding: 10px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 8px;
  font-size: 14px;
  color: white;
  font-weight: 600;
  cursor: pointer;
}

/* ========== 底部导航 ========== */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #e8e8e8;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  padding-bottom: env(safe-area-inset-bottom);
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 0;
  cursor: pointer;
}

.nav-item.active {
  color: #667eea;
}

.nav-icon {
  font-size: 20px;
  margin-bottom: 4px;
}

.nav-item span:last-child {
  font-size: 12px;
}
</style>
